import type { Immutable } from "immer";
import React, { useContext } from "react";
import { getReactivatedContext } from "tsi-common-react/src/utils/environment";
import { t } from "ttag";

import { Context } from "@reactivated";
import type {
    StoreDetailBlock as StoreDetailBlockValue,
    templates,
} from "@reactivated";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

/**
 * Faithful port of the legacy server-rendered
 * ``cms/blocks/flagship/store_detail.html`` (FlagshipPage ``StoreDetailBlock``).
 *
 * The block ``value`` carries the two support images (legacy
 * ``{% wagtail_image ... specs='fill-720x430' %}`` -> the ``fill-720x430``
 * rendition, eager ``<img>``, no srcset per the accepted convention) plus the
 * parking / find_us / close_by strings. The store-hours lead-in
 * (``page.title`` / ``page.hours_summary`` /
 * ``page.upcoming_holiday_hours_summary``) and the franchisee disclaimer
 * (``page.show_franchisee_disclaimer``) are PAGE-level, so they arrive as
 * props from the page body's ``store_detail_ssr`` side-channel. The three
 * media-object icons
 * are static assets rebuilt from ``STATIC_URL`` (CustomerServiceInfoBlock
 * precedent), ``alt=""`` as in the legacy ``{% static_image %}`` tag.
 */
// The page-level props are the page body's per-block ``store_detail_ssr``
// side-channel entry; derive the shape from it rather than restating it.
type IStoreDetailSSR =
    templates.FlagshipPageTemplate["store_detail_ssr"][string];

export const FlagshipStoreDetailBlock = (
    props: Immutable<{ value: StoreDetailBlockValue } & IStoreDetailSSR>,
): React.JSX.Element => {
    const v = props.value;
    const context = useContext(Context) || getReactivatedContext();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const staticURL = context?.STATIC_URL ?? "/static/";
    const holidayHours = props.upcoming_holiday_hours_summary;
    const storeImg = v.store_details_image;
    const mapImg = v.location_map;
    return (
        <div className="details">
            <div className="lead-in">
                <h3 className="lead-in__headline">
                    {interpolate(t`%(page_title)s Details`, {
                        page_title: props.page_title,
                    })}
                </h3>
                <div className="lead-in__subtext">
                    <p>
                        <strong>{t`Hours:`}</strong> {props.hours_summary}
                    </p>
                    {holidayHours.length > 0 ? (
                        <ul className="details__holiday-hours">
                            {holidayHours.map((line, i) => (
                                <li key={i}>
                                    <strong>{line.label}:</strong>{" "}
                                    {line.schedule}
                                </li>
                            ))}
                        </ul>
                    ) : null}
                </div>
            </div>
            <div className="details__images">
                <figure className="details__image">
                    {storeImg ? (
                        <img
                            src={
                                storeImg.renditions["fill-720x430"]?.url ??
                                storeImg.url
                            }
                            alt={storeImg.title}
                            loading="lazy"
                            decoding="async"
                        />
                    ) : null}
                </figure>
                <figure className="details__image">
                    {mapImg ? (
                        <img
                            src={
                                mapImg.renditions["fill-720x430"]?.url ??
                                mapImg.url
                            }
                            alt={mapImg.title}
                            loading="lazy"
                            decoding="async"
                        />
                    ) : null}
                </figure>
            </div>
            <div className="details__tips l-capped-width">
                <div className="media-object details__tip">
                    <img
                        className="media-object__image details__tip-image"
                        src={`${staticURL}img/flagship-page/icon-parking.png`}
                        alt=""
                        loading="lazy"
                        decoding="async"
                    />
                    <div className="media-object__body">
                        <h5 className="media-object__headline">{t`Best Places To Park`}</h5>
                        <p className="media-object__descript type_x0point875">
                            {v.parking}
                        </p>
                    </div>
                </div>
                <div className="media-object details__tip">
                    <img
                        className="media-object__image details__tip-image"
                        src={`${staticURL}img/flagship-page/icon-find.png`}
                        alt=""
                        loading="lazy"
                        decoding="async"
                    />
                    <div className="media-object__body">
                        <h5 className="media-object__headline">{t`How To Find Us`}</h5>
                        <p className="media-object__descript type_x0point875">
                            {v.find_us}
                        </p>
                    </div>
                </div>
                <div className="media-object details__tip">
                    <img
                        className="media-object__image details__tip-image"
                        src={`${staticURL}img/flagship-page/icon-closeby.png`}
                        alt=""
                        loading="lazy"
                        decoding="async"
                    />
                    <div className="media-object__body">
                        <h5 className="media-object__headline">{t`What's Close To Us`}</h5>
                        <p className="media-object__descript type_x0point875">
                            {v.close_by}
                        </p>
                    </div>
                </div>
            </div>

            {props.show_franchisee_disclaimer ? (
                <p className="details__disclaimer">{t`This store is independently owned and operated by a franchisee of Tempur Franchising U.S., LLC. All employees working at this store are employees of the franchisee, and not Tempur Franchising U.S., LLC or any of its affiliates.`}</p>
            ) : null}
        </div>
    );
};
